import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useAuth0 } from "@auth0/auth0-react";
import { useApp } from "../../AppProvider";
import { QUERY_OPTIONS_CACHE } from "../usersManagement/constants";

/** GET /api/water-level-records/my-wells
 *  Returns the list of wells for the current user.
 */
async function fetchMyWells(token) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/my-wells`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
}

/** GET /api/water-level-records/measurement-method
 * Returns the list of measurement methods.
 */
async function fetchMeasurementMethod(token) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/measurement-method`,
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return data;
}

/** GET /api/water-level-records/water-level-entry-list?well_ndx=123
 *  Returns all water-level records for a given well_ndx
 */
async function fetchWaterLevelEntries(token, well_ndx) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/water-level-entry-list`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { well_ndx },
    }
  );
  return data;
}

/** PUT /api/water-level-records/water-level-entry
 *  Body: { entry: {...} }
 *  Upserts a single water-level entry (creates if no ndx, updates if ndx)
 */
async function saveSingleWaterLevelEntry(token, entry) {
  const { data } = await axios.put(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/water-level-entry`,
    { entry },
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

/** DELETE /api/water-level-records/water-level-entry/:entryId
 *  Deletes a single water-level entry.
 */
async function deleteSingleWaterLevelEntry(token, entryId) {
  const { data } = await axios.delete(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/water-level-entry/${entryId}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  return data;
}

/** GET /api/water-level-records/contact-by-well?well_ndx=123
 * Returns contact information for a well based on its ndx.
 */
async function fetchContactByWell(token, well_ndx) {
  const { data } = await axios.get(
    `${process.env.REACT_APP_ENDPOINT}/api/water-level-records/contact-by-well`,
    {
      headers: { Authorization: `Bearer ${token}` },
      params: { well_ndx },
    }
  );
  return data;
}

/* ----------------------------------------------
   Main Hook
   ---------------------------------------------- */
export function useWaterLevelRecordsForm(selectedWellNdx) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { doToast } = useApp();

  // 1) My Wells
  const myWellsQuery = useQuery(["my-wells"], async () => {
    const token = await getAccessTokenSilently();
    return fetchMyWells(token);
  });

  const measurementMethodQuery = useQuery(
    ["measurement-method"],
    async () => {
      const token = await getAccessTokenSilently();
      return fetchMeasurementMethod(token);
    },
    QUERY_OPTIONS_CACHE
  );

  // 2) Water-Level Entries for the chosen well
  const waterLevelEntriesQuery = useQuery(
    ["water-level-entries", selectedWellNdx],
    async () => {
      if (!selectedWellNdx) return [];
      const token = await getAccessTokenSilently();
      return fetchWaterLevelEntries(token, selectedWellNdx);
    },
    {
      enabled: !!selectedWellNdx,
    }
  );

  // 3) Mutation to Save (Create/Update) a single water-level entry
  const saveWaterLevelMutation = useMutation(
    async (entry) => {
      const token = await getAccessTokenSilently();
      return saveSingleWaterLevelEntry(token, entry);
    },
    {
      onSuccess: (data) => {
        doToast("success", data?.message || "Water level entry saved!");
        // Invalidate so the table refreshes
        queryClient.invalidateQueries(["water-level-entries", selectedWellNdx]);
      },
      onError: (error) => {
        console.error("Error saving water level entry:", error);
        doToast(
          "error",
          error?.response?.data?.error || "Failed to save water level entry."
        );
      },
    }
  );

  // 4) Mutation to Delete a single water-level entry
  const deleteWaterLevelMutation = useMutation(
    async (entryId) => {
      const token = await getAccessTokenSilently();
      return deleteSingleWaterLevelEntry(token, entryId);
    },
    {
      onSuccess: (data) => {
        doToast("success", data?.message || "Water level entry deleted!");
        // Invalidate so the table refreshes
        queryClient.invalidateQueries(["water-level-entries", selectedWellNdx]);
      },
      onError: (error) => {
        console.error("Error deleting water level entry:", error);
        doToast(
          "error",
          error?.response?.data?.error || "Failed to delete water level entry."
        );
      },
    }
  );

  // 5) Fetch contact info by well
  const contactByWellQuery = useQuery(
    ["contact-by-well", selectedWellNdx],
    async () => {
      if (!selectedWellNdx) return [];
      const token = await getAccessTokenSilently();
      return fetchContactByWell(token, selectedWellNdx);
    },
    { enabled: !!selectedWellNdx }
  );

  return {
    myWellsQuery,
    measurementMethodQuery,
    waterLevelEntriesQuery,
    saveWaterLevelMutation,
    deleteWaterLevelMutation,
    contactByWellQuery,
  };
}
